import { BdGraphNode, BdGraph, BdGraphNodeStateMap, BdGraphStateMap, BdGraphPropertyValueMap } from './graph.model';
import { Injectable } from '@angular/core';
import { Elections } from './elections';
import { GraphUtils } from './graph-utils';
import { PropertyVotesModel } from './models/property-votes.model';
import { ElectionResultModel } from './models/election-result.model';

/*
Generic API for querying, editing, serializing nested objects.

State synchronization works only for boolean properties on BdGraphNode objects.

This is functionally a TREE graph, although could be extended to work for any DAG when that is required.

#TODO: [EM] IMPROVE efficiency of partial tree sync via early termination no change up the tree

*/

type IncludeIf = (id: string) => boolean;

type DoFunc = (id: string) => void;

// Functions for Querying and Editing BdGraph objects
@Injectable({
  providedIn: 'root'
})
export class BdGraphService {


  // recursively add children to node data
  private serializeNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, graphNode: TNode,
    nodeProperties: Object = {}, includeIf: IncludeIf = (id: string) => true): any {

      const node: any = {};

      // id
      node.id = graphNode.id;

      // boolean properties
      Object.entries(graphNode.boolState).forEach(([prop, value]) => {
        if (nodeProperties.hasOwnProperty(prop)) {
          node[prop] = value;
        }
      });

      // number properties
      Object.entries(graphNode.numState).forEach(([prop, value]) => {
        if (nodeProperties.hasOwnProperty(prop)) {
          node[prop] = value;
        }
      });


      // children
      if (graph.successors[graphNode.id]) {
          node.children = [];
          graph.successors[graphNode.id].forEach( (cid) => {
              if (includeIf(cid)) {
                node.children.push(this.serializeNode(graph, graph.nodes[cid], nodeProperties, includeIf));
              }

          }, this);
      }
      return node;
  }

  // create voter object holding the state to be propagated up


  // get all union of all siblings
  private getAllSiblingsFromList<TNode extends BdGraphNode>(graph: BdGraph<TNode>, targets: string[],
    inclusive?: boolean): string[] {
    const siblings: string[] = [];
    let i: number;
    for (i = 0; i < targets.length; i++) {
      this.getSiblings(graph, targets[i], inclusive).forEach((_sibling) => {
        if (!siblings.includes(_sibling)) {
          siblings.push(_sibling);
        }
      });
    }

    return siblings;
  }




  // this function must not delete voters[id]
  // but the callsite must have its voters variable updated appropriate to ensure full upward graph sync

  // [voting] propagate votes on all properties on all elections. Valid for TREE graphs only
  private getElectionResults<TNode extends BdGraphNode>(graph: BdGraph<TNode>,
    voters: BdGraphStateMap, elections: string[], propSyncMap: any): ElectionResultModel[] {


      ///////////////////
      // a refactor delt with chapter serializer and its concerns
      // did not yet refactor engine service concerns.
      // PropertyVotes was refactored to be referentially transparent
      // call site needs to be adjusted
      // did not adjust this call site.
      // tslint:disable-next-line
      class __TEMP_UNTIL_REFACTOR_PropertyVotes extends PropertyVotesModel {
        [key: string]: {
          votesFor: string[];
          votesAgainst: string[];
        };

        constructor(_propSyncMap: Object, __voters: Object) {
          super();
          Object.entries(_propSyncMap).forEach(([property, method]) => {
            this[property] = {votesFor: [], votesAgainst: []};
            Object.entries(__voters).forEach(([voterId, vote]) => {
              if (vote[property]) {
                this[property].votesFor.push(voterId);
              } else {
                this[property].votesAgainst.push(voterId);
              }
            }, this);
          }, this);
        }
      }
      ///////////////////




      let electionTallies: __TEMP_UNTIL_REFACTOR_PropertyVotes;
      let _voters: Object;
      const properties = Object.keys(propSyncMap);
      const results: ElectionResultModel[] = [];
      elections.forEach((electionId) => {
        // complete voters list
        _voters = {};
        GraphUtils.getSuccessors(graph, electionId).forEach((id) => {
          _voters[id] = voters[id] || Elections.createVoter(properties, GraphUtils.getState(graph, id, properties));
          delete voters[id];
        }, this);

        // count votes for and against for the relevant properties
        electionTallies = new __TEMP_UNTIL_REFACTOR_PropertyVotes(propSyncMap, _voters);

        // create election result from tallies
        // const er = new ElectionResult(electionId, electionTallies, propSyncMap);
        const er = Elections.buildElectionResult(electionId, electionTallies, propSyncMap);
        results.push(er);

        // register new election as voter for upward propagation
        const _voterstate = {};
        Object.entries(er).forEach(([property, result]) => {
          _voterstate[property] = result.result;
        });
        voters[electionId] = _voterstate;
      }, this);
      return results;
  }

  // [voting] update a graph state record for either whole state or changed state
  private updateGraphStateRecord<TNode extends BdGraphNode>(graph: BdGraph<TNode>, graphState: BdGraphStateMap,
    electionResults: ElectionResultModel[], properties: string[], wholeState: boolean = true): void {

      electionResults.forEach((er) => {
        const id = er[properties[0]].id;
        const nodeState = GraphUtils.getState(graph, id, properties);
        const targState = {};
        let needsRecording = false;
        properties.forEach((property) => {
          if (wholeState || er[property].result !== nodeState[property]) {
            targState[property] = er[property].result;
            needsRecording = true;
          }
        });

        if (needsRecording) {
          graphState[id] = targState;
        }
      }, this);
  }



  // #TODO[EM]: FIX create nested data from BdGraph object
  // TODO: remove unused
  // serialize<TNode extends BdGraphNode>(graph: BdGraph<TNode>, nodeProperties: Object = {}, includeIf: IncludeIf = (id) => true ): Object[] {
  //   const serialized = [];
  //   Object.entries(graph.roots).forEach(([nodeId, graphNode]) => {
  //     if (includeIf(nodeId)) {
  //       serialized.push(this.serializeNode(graph, graphNode, nodeProperties, includeIf));
  //     }
  //   }, this);

  //   return serialized;
  // }

  // #TODO[EM]: FIX create a new tree from the current tree
  // TODO: remove unused
  // copy<TNode extends BdGraphNode>(graph: BdGraph<TNode>, nodeProperties: Object = {},
  //   includeIf: IncludeIf = (id) => true ): BdGraph<TNode> {
  //     const data = this.serialize(graph, nodeProperties, includeIf);
  //     return this.createFromSrc(data);
  // }



  // returns true if node id exists
  nodeExists<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): boolean {
    return !!graph.nodes[id];
  }






  // return all leaf-node ids
  getLeaves<TNode extends BdGraphNode>(graph: BdGraph<TNode>): string[] {
    const leaves = [];
    Object.entries(graph.nodes).forEach(([nodeId, node]) => {
        if (!graph.successors[nodeId]) {
            leaves.push(nodeId);
        }
    });
    return leaves;
  }



  // return all node ids beneath target node
  getDescendants<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, inclusive: boolean = false): string[] {
    const descendants = [];
    const self = this;
    function stepDown(tid: string) {
        GraphUtils.getSuccessors(graph, tid).forEach((childId) => {
            descendants.push(childId);
            stepDown(childId);
        });
    }

    // traverse
    stepDown(id);

    // add self id if inclusive
    if (inclusive) {
      descendants.push(id);
    }

    return descendants;
  }

  // return all leaf-node ids beneath or at the target node
  getLeafDescendants<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, inclusive: boolean = false): string[] {
    const leaves = this.getDescendants(graph, id, inclusive).filter((descendantId) => {
        return GraphUtils.isLeaf(graph, descendantId);
    });

    if (GraphUtils.isLeaf(graph, id) || inclusive) {
      leaves.push(id);
    }
    return leaves;
  }

  getAllLeafDescendants<TNode extends BdGraphNode>(graph: BdGraph<TNode>, ids: string[]): string[] {
    let leaves = [];

    ids.forEach(id => {
      if (GraphUtils.isLeaf(graph, id)) {
        leaves = [...leaves, id];
      } else {
        leaves = [...leaves, ...this.getLeafDescendants(graph, id, false)];
      }
    });

    // De-dupe
    return leaves
      .sort()
      .reduce((result, id) => {
        if (result.indexOf(id) < 0) {
          result.push(id);
        }
        return result;
      }, []);
  }

  getInverseSelection<TNode extends BdGraphNode>(graph: BdGraph<TNode>, ids: string[]): string[] {
    const descendants = this.getAllLeafDescendants(graph, ids);

    return this.getLeaves(graph)
      .filter(leaf => descendants.indexOf(leaf) < 0);
  }

  // return all node ids at the same tree level as target node
  getSiblings<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, inclusive: boolean = false): string[] {
    const parentId = graph.predecessors[id];
    let siblings = [];
    if (!!parentId) {
        // non-root node
        siblings = GraphUtils.getSuccessors(graph, parentId);
    } else {
        // root node
        siblings = GraphUtils.getRoots(graph);
    }

    if (!inclusive) {
        const idx = siblings.indexOf(id);
        if (idx >= 0) {
            siblings.splice(idx, 1);
        }
    }
    return siblings;
  }


  // get node data if it exists, for all ids in list
  getNodes<TNode extends BdGraphNode>(graph: BdGraph<TNode>, targets: string[]): TNode[] {
    const nodes: TNode[] = [];
    targets.forEach((id) => {
      if (graph.nodes[id]) {
        nodes.push(graph.nodes[id]);
      }
    });
    return nodes;
  }

  // set state on a graph node and return changes
  // engine state and service
  setState<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, state: BdGraphNodeStateMap): BdGraphNodeStateMap {
    const node = GraphUtils.getNode(graph, id);
    const changes: BdGraphNodeStateMap = {};
    Object.entries(state).forEach(([property, value]) => {
      if (typeof value === 'boolean' && node.boolState[property] !== value) {
        node.boolState[property] = value;
        changes[property] = value;
      } else if (typeof value === 'number' && node.numState[property] !== value) {
        node.numState[property] = value;
        changes[property] = value;
      }
    });
    return changes;
  }

  // synchronize all specified properties on the whole tree according
  // used by engine state and service
  synchronize<TNode extends BdGraphNode>(graph: BdGraph<TNode>, syncProperties: {[key: string]: string},
    leafStateMap?: BdGraphStateMap): BdGraphStateMap {

    // get results from upward graph traversal
    const graphStateResults = this.calculateGraphStateMap(graph, syncProperties, leafStateMap);

    // apply state changes to graph
    Object.entries(graphStateResults).forEach(([id, state]) => {
      this.setState(graph, id, state);
    }, this);

    return graphStateResults;
  }

  // calculate state of all affected leaves via single, full graph traversal
  // only used by serialize-chapter service
  // state of all leaves


  // calculate but do not modify graph BOOLEAN state values via upward voting algorithm
  // used by engine state and service
  calculateGraphStateMap<TNode extends BdGraphNode>(graph: BdGraph<TNode>, syncProperties: {[key: string]: string},
    leafStateMap?: BdGraphStateMap, wholeState = !leafStateMap): BdGraphStateMap {

    let elections: string[] = []; // node-ids on which to next set the state
    const voterState: BdGraphStateMap = {}; // node state for voters
    let electionResults: ElectionResultModel[] = []; // Array of election result objects
    const graphStateRecord: BdGraphStateMap = {}; // record of graph state


    // initialize voterState and graphStateRecord
    const _properties: string[] = Object.keys(syncProperties);
    let depth = 0;
    if (leafStateMap) {
      // partial graph traversal of modified leaves and their siblings up the tree, only record changes
      Object.entries(leafStateMap).forEach(([id, leafState]) => {
        voterState[id] = Elections.createVoter(_properties, leafStateMap[id]);
        graphStateRecord[id] = {...voterState[id]};
        depth = Math.max(depth, GraphUtils.getNode(graph, id).depth);
      }, this);
    } else {
      // full graph traversal from all leaves
      this.getLeaves(graph).forEach((id) => {
        voterState[id] = Elections.createVoter(_properties, GraphUtils.getNode(graph, id).boolState);
        graphStateRecord[id] = {...voterState[id]};
        depth = Math.max(depth, GraphUtils.getNode(graph, id).depth);
      }, this);
    }

    // hold elections up the tree until we run out
    depth -= 1;
    elections = Elections.getEligibleElections(graph, Object.keys(voterState), depth);
    while (depth >= 0) {
        electionResults = this.getElectionResults(graph, voterState, elections, syncProperties);
        this.updateGraphStateRecord(graph, graphStateRecord, electionResults, _properties, wholeState);
        depth -= 1;
        elections = Elections.getEligibleElections(graph, Object.keys(voterState), depth);
    }

    // return full graph state and the condensed graph state
    return graphStateRecord;
  }


  // [query] get the lowest-depth smallest subset of accessors from list of accessors
  // used by chapters state
  filterGraphAccessors<TNode extends BdGraphNode>(graph: BdGraph<TNode>, accessors: string[]): string[] {
    // get nodes
    const accessorNodes = this.getNodes(graph, accessors);

    // sort by depth
    const sorted = accessorNodes.sort((left, right) => left.depth - right.depth);

    // track accessible
    const accessible = new Map<string, boolean>();
    const filtered = sorted.filter((accessorNode) => {
      if (!accessible.has(accessorNode.id)) {
        // register descendants as accessible
        this.getDescendants(graph, accessorNode.id, true).forEach((id) => accessible.set(id, true));
        return true;
      } else {
        // ignore, node already accessible
        return false;
      }
    });

    return filtered.map((node) => node.id);
  }

  // [edit] after editing, the depth should be reset
  // engine service
  resetDepth<TNode extends BdGraphNode>(graph: BdGraph<TNode>): void {
    // handle reset of depth
    const _resetDepth: DoFunc = (id: string) => {
      let depth = 0;
      if (graph.predecessors[id]) {
        depth = GraphUtils.getNode(graph, graph.predecessors[id]).depth + 1;
      }
      GraphUtils.getNode(graph, id).depth = depth;
    };

    GraphUtils.depthFirst(graph, _resetDepth);
  }

  // [edit] deep/soft delete a node and its references
  // engine service
  deleteNode<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string, deep: boolean = true): void {
    if (!this.nodeExists(graph, id)) {
      return;
    }

    // delete node
    delete graph.nodes[id];

    // delete predecssor edges : remove from parent's children
    const predecessor: string = graph.predecessors[id];
    if (predecessor) {
        delete graph.predecessors[id];
        if (graph.successors[predecessor]) {
            graph.successors[predecessor] = graph.successors[predecessor].filter((sid) => sid !== id);
        }
    }

    // delete successor edges: remove children (recursively)
    const successors: string[] = graph.successors[id];
    if (successors) {
        delete graph.successors[id];
        if (deep) {
          successors.forEach((cid) => { this.deleteNode(graph, cid); }, this);
        }
    }

    // delete from root
    if (graph.roots[id]) {
      delete graph.roots[id];
    }
  }

  sanitizeNodeID(id: string) {
    const regex = new RegExp(/\|/, 'gi');
    return id.replace(regex, '_');
  }

  // [edit] set a node as a leaf node, return all removed nodes
  // used by engine service
  resetAsLeaf<TNode extends BdGraphNode>(graph: BdGraph<TNode>, id: string): string[] {
    let toRemove: string[] = [];
    if (graph.nodes[id] && !GraphUtils.isLeaf(graph, id)) {
        // get all descendents; these will be removed
        toRemove = this.getDescendants(graph, id);

        // remove descendent nodes from list
        delete graph.successors[id]; // it's a leaf node now
        toRemove.forEach((rid) => {
            delete graph.nodes[rid];
            delete graph.successors[rid];
            delete graph.predecessors[rid];
        });

    }
    return toRemove;
  }

}
